/**
 * Manager Email OTP Code Verification Page
 * Handles verification of one-time email codes for login and registration
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  managerVerifyEmailCode,
  managerVerifyRegistrationEmailCode,
  managerRequestEmailCode,
  managerRequestRegistrationEmailCode,
  managerSession,
  ApiClientError,
} from '../../lib/api';

import { useAuth } from '../../hooks/useAuth';
import { createLogger } from '@resida/shared';
import { Button } from '../../components/ui/button';
import { validateReturnTo } from '../../lib/return-to';

const logger = createLogger('landlord:pages:auth:EmailCodeVerifyPage');

interface LocationState {
  email: string;
  purpose: 'login' | 'register';
  /** Where to land after a successful login (the page that required authentication). */
  returnTo?: string | null;
  /** Policy consent captured on RegisterPage — the sole consent checkpoint for registration. */
  policies?: Array<{ policyType: string; policyVersion: string }>;
}

export function EmailCodeVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();

  const state = location.state as LocationState | null;
  const email = state?.email ?? '';
  const purpose = state?.purpose ?? 'login';
  const policies = state?.policies ?? [];

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate('/login', { replace: true });
    }
  }, [email, navigate]);

  // Focus the input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (code.length === 6) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError(null);
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (code.length < 6 || isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      logger.info('Verifying email code', { purpose });

      if (purpose === 'register') {
        await managerVerifyRegistrationEmailCode(email, code, policies);
      } else {
        await managerVerifyEmailCode(email, code);
      }

      // Fetch full session and update auth context
      const session = await managerSession();
      if (session) {
        setSession(session);
      }

      logger.info('Email code verification successful');
      navigate(validateReturnTo(state?.returnTo), { replace: true });
    } catch (err) {
      logger.error('Email code verification failed:', err);

      if (err instanceof ApiClientError) {
        if (err.code === 'INVALID_CODE' || err.code === 'CODE_EXPIRED') {
          setError('Invalid or expired code. Please try again or request a new code.');
        } else if (err.code === 'CODE_ALREADY_USED') {
          setError('This code has already been used. Please request a new code.');
        } else if (err.code === 'RATE_LIMITED') {
          setError('Too many attempts. Please wait a moment and try again.');
        } else {
          setError(err.message || 'Verification failed. Please try again.');
        }
      } else if (err instanceof Error) {
        setError(err.message || 'An unexpected error occurred. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }

      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResend() {
    if (resendLoading) return;

    setResendLoading(true);
    setResendSuccess(false);
    setError(null);

    try {
      if (purpose === 'register') {
        await managerRequestRegistrationEmailCode(email);
      } else {
        await managerRequestEmailCode(email);
      }
      setResendSuccess(true);
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      logger.error('Resend email code failed:', err);
      if (err instanceof ApiClientError) {
        setError(err.message || 'Failed to send a new code. Please try again.');
      } else {
        setError('Failed to send a new code. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  }

  if (!email) return null;

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-sm border border-border p-6 md:p-8">
          <h1 className="text-2xl font-semibold text-foreground mb-2 text-center">
            Enter Your Code
          </h1>
          <p className="text-sm text-muted-foreground mb-1 text-center">
            We sent a 6-digit code to
          </p>
          <p className="text-sm font-medium text-foreground mb-6 text-center">
            {email}
          </p>

          {error && (
            <div
              className="text-sm text-destructive p-3 bg-destructive/5 border border-destructive/20 rounded-md mb-4"
              role="alert"
              data-testid="email-code-error"
            >
              {error}
            </div>
          )}

          {resendSuccess && (
            <div
              className="text-sm text-success p-3 bg-success/5 border border-success/20 rounded-md mb-4"
              role="status"
            >
              A new code has been sent to {email}.
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="otp-code" className="text-sm font-medium text-muted-foreground">
                6-Digit Code
              </label>
              <input
                ref={inputRef}
                id="otp-code"
                name="one-time-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                disabled={isLoading}
                required
                aria-required="true"
                aria-invalid={error !== null}
                aria-describedby={error ? 'email-code-error' : undefined}
                className="w-full min-h-[44px] px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring disabled:opacity-60 text-center text-xl tracking-widest font-mono"
                data-testid="email-code-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter the 6-digit code from your email. The code will expire in 10 minutes.
              </p>
            </div>

            <Button
              type="submit"
              disabled={isLoading || code.length < 6}
              className="w-full min-h-[44px]"
              data-testid="email-code-submit"
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Didn't receive a code?
            </p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading}
              className="text-sm text-primary hover:text-primary/80 underline disabled:opacity-60"
              data-testid="email-code-resend"
            >
              {resendLoading ? 'Sending...' : 'Request a new code'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate(purpose === 'register' ? '/register' : '/login')}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Back to {purpose === 'register' ? 'registration' : 'login'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailCodeVerifyPage;
